import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import DragNumberInput, { formatCompactNumberInputValue } from "../ui/DragNumberInput";
import FieldLabel from "../ui/FieldLabel";
import MenuSelect from "../ui/MenuSelect";
import Separator from "../ui/Separator";
import Tooltip from "../ui/Tooltip";
import { INSPECTOR_CHROME_SECTION_ICON_CLASS } from "../../utils/inspectorChromeButtonClasses";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";

const BALL_ACTUATOR_AXES = [
  { key: "x", label: "X", gear: [1, 0, 0] },
  { key: "y", label: "Y", gear: [0, 1, 0] },
  { key: "z", label: "Z", gear: [0, 0, 1] },
];

const getBallActuatorAxisKey = (actuator) => {
  const gear = Array.isArray(actuator?.gear) ? actuator.gear : [Number(actuator?.gear) || 1, 0, 0];
  const values = [Number(gear[0]) || 0, Number(gear[1]) || 0, Number(gear[2]) || 0];
  const maxIndex = values.reduce(
    (bestIdx, value, idx) => (Math.abs(value) > Math.abs(values[bestIdx]) ? idx : bestIdx),
    0
  );
  return BALL_ACTUATOR_AXES[maxIndex]?.key ?? "x";
};

const getBallActuatorStrength = (actuator) => {
  const gear = Array.isArray(actuator?.gear) ? actuator.gear : [Number(actuator?.gear) || 1, 0, 0];
  const values = [Number(gear[0]) || 0, Number(gear[1]) || 0, Number(gear[2]) || 0];
  const maxIndex = values.reduce(
    (bestIdx, value, idx) => (Math.abs(value) > Math.abs(values[bestIdx]) ? idx : bestIdx),
    0
  );
  const strength = values[maxIndex];
  return Number.isFinite(strength) && Math.abs(strength) > 1e-12 ? strength : 1;
};

const buildBallActuatorGear = (axisKey, strength = 1) => {
  const axis = BALL_ACTUATOR_AXES.find((entry) => entry.key === axisKey) ?? BALL_ACTUATOR_AXES[0];
  const gain = Number.isFinite(Number(strength)) ? Number(strength) : 1;
  return axis.gear.map((component) => component * gain);
};

const getBallAxisActuatorName = (jointName, axisKey) => {
  const axis = BALL_ACTUATOR_AXES.find((entry) => entry.key === axisKey) ?? BALL_ACTUATOR_AXES[0];
  return `${jointName || "ball_joint"}_${axis.key}_torque`;
};

const shouldAutoRenameBallActuator = (actuatorName, jointName) => {
  const name = typeof actuatorName === "string" ? actuatorName : "";
  return BALL_ACTUATOR_AXES.some((axis) => name === getBallAxisActuatorName(jointName, axis.key));
};

function BallJointActuatorsSection({ disabled, joint, jointActuators, sceneActions }) {
  const handleAddBallActuator = () => {
    const axis = BALL_ACTUATOR_AXES[0];
    if (!axis || typeof sceneActions?.addActuator !== "function") return;
    sceneActions.addActuator({
      name: getBallAxisActuatorName(joint.name, axis.key),
      kind: "motor",
      joint: joint.uuid,
      ctrlrange: [-1, 1],
      gear: axis.gear,
    });
  };

  const handlePatchActuator = (actuatorUuid, patch) => {
    if (!actuatorUuid || typeof sceneActions?.updateActuator !== "function") return;
    sceneActions.updateActuator(actuatorUuid, patch);
  };

  const handleRemoveActuator = (actuatorUuid) => {
    if (!actuatorUuid || typeof sceneActions?.removeActuator !== "function") return;
    sceneActions.removeActuator(actuatorUuid);
  };

  return (
    <InspectorPhysicsLockedCollapsibleWrap locked={disabled}>
      <Collapsible defaultOpen>
        <CollapsibleTrigger
          quiet
          actions={
            <Tooltip content={disabled ? "Stop physics to add an actuator" : "Add actuator"}>
              <button
                type="button"
                disabled={disabled}
                aria-label="Add ball actuator"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!disabled) handleAddBallActuator();
                }}
                className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
                style={{ opacity: disabled ? 0.35 : 0.55 }}
              >
                <Plus size={16} aria-hidden="true" />
              </button>
            </Tooltip>
          }
        >
          Ball Actuators
        </CollapsibleTrigger>
        <CollapsibleContent>
          <fieldset
            disabled={disabled}
            className={
              disabled
                ? "pt-2 space-y-4 text-sm text-gray-300 opacity-60 pointer-events-none"
                : "pt-2 space-y-4 text-sm text-gray-300"
            }
          >
            {jointActuators.length === 0 ? (
              <FieldLabel>Add an actuator, then choose which rotation axis it controls.</FieldLabel>
            ) : null}

            {jointActuators.map((actuator, idx) => {
              const axisKey = getBallActuatorAxisKey(actuator);
              const strength = getBallActuatorStrength(actuator);
              const ctrlMin = Array.isArray(actuator.ctrlrange)
                ? Number(actuator.ctrlrange[0])
                : -1;
              const ctrlMax = Array.isArray(actuator.ctrlrange) ? Number(actuator.ctrlrange[1]) : 1;
              return (
                <div key={actuator.uuid || actuator.name || idx} className="space-y-3">
                  {idx > 0 ? <Separator /> : null}
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-200">
                      {actuator.name || "Ball actuator"}
                    </span>
                    <Tooltip
                      content={disabled ? "Stop physics to delete actuator" : "Delete actuator"}
                    >
                      <button
                        type="button"
                        disabled={disabled}
                        aria-label="Delete actuator"
                        onClick={() => handleRemoveActuator(actuator.uuid)}
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-red-900/60 text-red-300 hover:bg-red-900/20 disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </Tooltip>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <FieldLabel>Axis</FieldLabel>
                      <MenuSelect
                        value={axisKey}
                        onChange={(nextAxis) => {
                          const patch = {
                            gear: buildBallActuatorGear(nextAxis, strength),
                          };
                          if (shouldAutoRenameBallActuator(actuator.name, joint.name)) {
                            patch.name = getBallAxisActuatorName(joint.name, nextAxis);
                          }
                          handlePatchActuator(actuator.uuid, patch);
                        }}
                        disabled={disabled}
                        options={BALL_ACTUATOR_AXES.map((axis) => ({
                          value: axis.key,
                          label: axis.label,
                        }))}
                        aria-label="Ball actuator axis"
                      />
                    </div>
                    <DragNumberInput
                      label="Strength"
                      value={strength}
                      dragValue={strength}
                      onDragValueChange={(value) =>
                        handlePatchActuator(actuator.uuid, {
                          gear: buildBallActuatorGear(axisKey, value),
                        })
                      }
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (Number.isFinite(value)) {
                          handlePatchActuator(actuator.uuid, {
                            gear: buildBallActuatorGear(axisKey, value),
                          });
                        }
                      }}
                      tooltipContent="Torque scaling along the selected ball-joint axis."
                      wrapperClassName="space-y-1"
                      labelClassName="inline-flex items-center gap-1 text-xs panel-text-helper"
                      inputClassName="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm"
                      inputProps={{ step: "0.1", disabled }}
                    />
                  </div>

                  <div>
                    <FieldLabel className="block mb-1">Control Range</FieldLabel>
                    <div className="grid grid-cols-2 gap-2">
                      <DragNumberInput
                        label="Min"
                        value={Number.isFinite(ctrlMin) ? ctrlMin : -1}
                        dragValue={Number.isFinite(ctrlMin) ? ctrlMin : -1}
                        onDragValueChange={(value) => {
                          if (Number.isFinite(Number(value))) {
                            handlePatchActuator(actuator.uuid, {
                              ctrlrange: [Number(value), Number.isFinite(ctrlMax) ? ctrlMax : 1],
                            });
                          }
                        }}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (Number.isFinite(value)) {
                            handlePatchActuator(actuator.uuid, {
                              ctrlrange: [value, Number.isFinite(ctrlMax) ? ctrlMax : 1],
                            });
                          }
                        }}
                        wrapperClassName="space-y-1"
                        labelClassName="inline-flex items-center gap-1 text-xs panel-text-helper"
                        inputClassName="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm"
                        displayValueFormatter={formatCompactNumberInputValue}
                        inputProps={{ step: "0.1", disabled }}
                      />
                      <DragNumberInput
                        label="Max"
                        value={Number.isFinite(ctrlMax) ? ctrlMax : 1}
                        dragValue={Number.isFinite(ctrlMax) ? ctrlMax : 1}
                        onDragValueChange={(value) => {
                          if (Number.isFinite(Number(value))) {
                            handlePatchActuator(actuator.uuid, {
                              ctrlrange: [Number.isFinite(ctrlMin) ? ctrlMin : -1, Number(value)],
                            });
                          }
                        }}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (Number.isFinite(value)) {
                            handlePatchActuator(actuator.uuid, {
                              ctrlrange: [Number.isFinite(ctrlMin) ? ctrlMin : -1, value],
                            });
                          }
                        }}
                        wrapperClassName="space-y-1"
                        labelClassName="inline-flex items-center gap-1 text-xs panel-text-helper"
                        inputClassName="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm"
                        displayValueFormatter={formatCompactNumberInputValue}
                        inputProps={{ step: "0.1", disabled }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </fieldset>
        </CollapsibleContent>
      </Collapsible>
    </InspectorPhysicsLockedCollapsibleWrap>
  );
}

export default React.memo(BallJointActuatorsSection);
